import { useCallback, useEffect, useMemo, useState } from 'react';
import { cartTotals, offerPercent } from '../utils/money';
import { itemFromLine, lineFromItem, serviceKey } from '../utils/services';

function offersFromBill(bill) {
  const meta = bill?.metadata || {};
  return {
    total_discount_percent: offerPercent(meta.total_discount_percent),
    total_voucher_percent: offerPercent(meta.total_voucher_percent),
  };
}

/** Editable order for the services step; reset from the saved bill whenever its lines change. */
export default function useBillCart(bill) {
  const [lines, setLines] = useState([]);
  const [offers, setOffers] = useState(offersFromBill(null));
  const [dirty, setDirty] = useState(false);
  const itemsSig = JSON.stringify((bill?.items || []).map((i) => [i.id, i.name, i.quantity, i.unit_price, i.stylist_name]));

  useEffect(() => {
    if (!bill) return;
    setLines((bill.items || []).map(lineFromItem));
    setOffers(offersFromBill(bill));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.id, itemsSig]);

  const edit = useCallback((fn) => {
    setLines(fn);
    setDirty(true);
  }, []);

  const toggle = useCallback(
    (svc) => {
      const key = serviceKey(svc.name);
      edit((prev) =>
        prev.some((l) => l.key === key)
          ? prev.filter((l) => l.key !== key)
          : [
              ...prev,
              {
                key,
                name: svc.name,
                unit_price: Number(svc.unit_price) || 0,
                quantity: 1,
                stylist_name: prev[prev.length - 1]?.stylist_name || '',
                service_id: svc.id || null,
                category: svc.category || null,
                discount_percent: 0,
                voucher_percent: 0,
                price_missing: svc.unit_price == null,
              },
            ],
      );
    },
    [edit],
  );

  const update = useCallback((line) => edit((prev) => prev.map((l) => (l.key === line.key ? line : l))), [edit]);
  const remove = useCallback((key) => edit((prev) => prev.filter((l) => l.key !== key)), [edit]);
  const changeOffers = useCallback((next) => {
    setOffers(next);
    setDirty(true);
  }, []);

  /** After a branch change: take each line's price from the new branch list when the name matches. */
  const reprice = useCallback(
    (services) => {
      const byKey = new Map(services.map((s) => [serviceKey(s.name), s]));
      edit((prev) =>
        prev.map((l) => {
          const hit = byKey.get(l.key);
          return hit && hit.unit_price != null
            ? { ...l, unit_price: Number(hit.unit_price), service_id: hit.id || l.service_id, price_missing: false }
            : { ...l, price_missing: true };
        }),
      );
    },
    [edit],
  );

  const taxRate = bill?.tax_rate ?? 0.18;
  const totals = useMemo(
    () => ({
      ...cartTotals(lines, {
        taxRate,
        totalDiscountPercent: offers.total_discount_percent,
        totalVoucherPercent: offers.total_voucher_percent,
      }),
      taxRate,
    }),
    [lines, offers, taxRate],
  );

  const missingPrice = lines.some((l) => !(Number(l.unit_price) > 0));

  return {
    lines,
    offers,
    dirty,
    totals,
    missingPrice,
    toggle,
    update,
    remove,
    reprice,
    setOffers: changeOffers,
    payload: () => ({ mode: 'replace', items: lines.map(itemFromLine), ...offers }),
  };
}
